// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * Who wrote a row: a user or a service account, with the id, kind, name and avatar a cell renders. Embedded wherever a row names its writer, as `createdBy`, `updatedBy` and `deletedBy`; null once the actor is gone.
 */
export const zActorRef = z.object({
  id: z.string(),
  kind: z.enum(['user', 'service']),
  name: z.string(),
  slug: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
});

/**
 * The smallest user shape: id, name, slug and avatar. Embedded wherever a response names a user: a member list, the actor of a notification. A row names its writers as `ActorRef`.
 */
export const zUserMinimalBase = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  thumbnailUrl: z.string().nullable(),
  entityType: z.enum(['user']),
});

/**
 * The user fields shown to every user who shares an organization with this one: name, slug, profile images, description and email address. User lookups and member lists return it with the last-seen time added; member lists add the membership too.
 */
export const zUserBase = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  slug: z.string(),
  thumbnailUrl: z.string().nullable(),
  bannerUrl: z.string().nullable(),
  description: z.string().nullable(),
  email: z.email(),
  entityType: z.enum(['user']),
});

/**
 * The fields every channel entity shares, such as organization; a channel owns memberships and roles. Used where a response names a channel without its details, such as the channel an invitation is for.
 */
export const zChannelBase = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  tenantId: z.string(),
  entityType: z.enum(['organization', 'workspace', 'project']),
  slug: z.string(),
  thumbnailUrl: z.string().nullable(),
  bannerUrl: z.string().nullable(),
});

/**
 * The fields every product entity shares, such as attachment: user-facing content that inherits access from its channel and syncs to clients. No endpoint returns it on its own; every product response starts with these fields and adds its writers as `ActorRef`s.
 */
export const zProductBase = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  description: z.string().nullable(),
  entityType: z.enum(['task', 'label', 'attachment']),
  keywords: z.string(),
});

/**
 * A user's membership in a channel without its audit fields: the role it grants, plus the member's own archive, mute and menu order. Returned for the signed-in user's own memberships, and as `included.membership` on a channel they belong to.
 */
export const zMembershipBase = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  channelType: z.enum(['organization', 'workspace', 'project']),
  channelId: z.uuid(),
  userId: z.uuid(),
  role: z.enum(['admin', 'member', 'guest']),
  archived: z.boolean(),
  muted: z.boolean(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327),
  organizationId: z.uuid(),
  workspaceId: z.uuid().nullable(),
  projectId: z.uuid().nullable(),
});

/**
 * The sync envelope on every product write: a mutation ID, a source ID and per-field timestamps. Clients send it with each create and update; the server merges concurrent edits by its timestamps, and a client recognizes its own writes coming back by the source ID.
 */
export const zStxBase = z.object({
  mutationId: z.string().max(36),
  sourceId: z.string().max(64),
  fieldTimestamps: z.record(z.string(), z.string()),
  replayed: z.boolean().optional(),
});

/**
 * A live notification that a product entity or a membership changed, naming the entity, its path and sequence range rather than the row. It arrives as a `change` event on `/entities/app/stream`; the client then fetches the changed rows through the regular API.
 */
export const zStreamNotification = z.object({
  kind: z.enum(['product', 'membership']),
  action: z.enum(['create', 'update', 'delete', 'moveOut']),
  productType: z.enum(['task', 'label', 'attachment']).nullable(),
  resourceType: z
    .enum(['request', 'membership', 'inactive_membership', 'tenant', 'system_role', 'service_account', 'api_key', 'oauth_client'])
    .nullable(),
  subjectId: z.string().nullable(),
  organizationId: z.string().nullable(),
  tenantId: z.string().nullable(),
  channelType: z.enum(['organization', 'workspace', 'project']).nullable(),
  path: z.string().nullable(),
  seq: z.int().nullable(),
  channelId: z.string().nullable(),
  stx: zStxBase.nullable(),
  batchUntilSeq: z.int().nullable(),
  count: z.int().nullable(),
  spreadWindow: z.int().nullable(),
  propagation: z
    .object({
      embeddedProduct: z.enum(['task', 'label', 'attachment']),
      hostProduct: z.enum(['task', 'label', 'attachment']),
      hostColumn: z.string(),
      update: z.array(z.string()),
      remove: z.array(z.string()),
    })
    .nullable(),
});

/**
 * The body of every error response. `type` is a stable code to branch on, `message` is readable text, and `requestId` identifies the request in the server logs.
 */
export const zApiError = z.object({
  name: z.string(),
  message: z.string(),
  type: z.string(),
  status: z.int().gte(400).lte(599),
  severity: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']),
  entityType: z.enum(['user', 'organization', 'workspace', 'project', 'task', 'label', 'attachment']).optional(),
  requestId: z.string().optional(),
  path: z.string().optional(),
  method: z.string().optional(),
  timestamp: z.string().optional(),
  userId: z.string().optional(),
  organizationId: z.string().optional(),
  meta: z.record(z.string(), z.union([z.number(), z.string(), z.array(z.string()), z.boolean()]).nullable()).optional(),
});

/**
 * The request is malformed or fails validation (400). `type` names the problem, such as `invalid_request`.
 */
export const zBadRequestError = zApiError.and(
  z.object({
    status: z.literal(400).optional(),
  }),
);

/**
 * The request carries no valid session, API key or access token, or it expired (401). Sign in again, or send a valid key or token.
 */
export const zUnauthorizedError = zApiError.and(
  z.object({
    status: z.literal(401).optional(),
  }),
);

/**
 * The caller is known but may not do this (403): their role denies the action, a recent step-up is required, or the action is refused while impersonating. `type` names the reason, such as `step_up_required`.
 */
export const zForbiddenError = zApiError.and(
  z.object({
    status: z.literal(403).optional(),
  }),
);

/**
 * The resource does not exist, or the caller may not read it (404). Both get the same answer, so a response never confirms that an id exists.
 */
export const zNotFoundError = zApiError.and(
  z.object({
    status: z.literal(404).optional(),
  }),
);

/**
 * The request conflicts with the current state (409), such as a link that belongs to another account (`user_mismatch`) or a create already in progress. Reload the current state before retrying.
 */
export const zConflictError = zApiError.and(
  z.object({
    status: z.literal(409).optional(),
  }),
);

/**
 * A rate limit refused the request (429). The `Retry-After` header says when to try again; an operation's rate limiters show what is counted.
 */
export const zTooManyRequestsError = zApiError.and(
  z.object({
    status: z.literal(429).optional(),
  }),
);

/**
 * The sign-in entry of one institution at one organization: the federation it goes through, the institution, the organization, and whether it is active. Public by the connection's id, which is the link an institution shares.
 */
export const zSsoEntry = z.object({
  id: z.string(),
  status: z.enum(['pending', 'active', 'disabled']),
  federation: z.object({
    key: z.string(),
    label: z.string(),
  }),
  institution: z.object({
    displayName: z.string(),
    logoUrl: z.string().nullable(),
  }),
  organization: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      thumbnailUrl: z.string().nullable(),
    })
    .nullable(),
});

/**
 * A tenant's trust in an external party that asserts user identities: an institution reached through an SSO federation, whose members sign in to the tenant's organization. Its id is the public key of the tenant's SSO entry page. System admins manage connections per tenant; `pending` until the institution activated the service at the federation.
 */
export const zConnection = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  kind: z.enum(['sso']),
  issuer: z.string().max(255),
  claimValues: z.array(z.string()),
  displayName: z.string().max(255),
  status: z.enum(['pending', 'active', 'disabled']),
  jitProvisioning: z.boolean(),
  config: z.object({
    idpEntityIds: z.array(z.string().min(1).max(255)).optional(),
    logoUrl: z.url().max(255).optional(),
  }),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
});

/**
 * A full user account: profile, preferences such as language and newsletter, the MFA setting and activity timestamps. Returned to the user themselves and to system admins; other users see the `UserBase` fields.
 */
export const zUser = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  entityType: z.enum(['user']),
  name: z.string().max(255),
  description: z.string().nullable(),
  slug: z.string().max(255),
  thumbnailUrl: z.string().max(2048).nullable(),
  bannerUrl: z.string().max(2048).nullable(),
  email: z.email(),
  mfaRequired: z.boolean(),
  firstName: z.string().max(255).nullable(),
  lastName: z.string().max(255).nullable(),
  language: z.enum(['en', 'nl']),
  newsletter: z.boolean(),
  contrast: z.enum(['system', 'more']),
  userFlags: z.object({
    finishedOnboarding: z.boolean(),
  }),
  updatedAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  lastSignInAt: z.string().nullable(),
});

/**
 * The signed-in user, with whether they have system admin access on this request and, in an impersonation, the system admin acting as them. A client reads it to learn who is signed in.
 */
export const zMe = z.object({
  user: zUser,
  isSystemAdmin: z.boolean(),
  impersonator: zUserMinimalBase.nullable(),
});

/**
 * How the signed-in user signs in: connected OAuth providers, the institutions of their organizations (connected or not), passkeys, whether TOTP is set up, and their live sessions. The account page lists it, where sessions can be ended and sign-in methods changed.
 */
export const zMeAuthData = z.object({
  enabledOAuth: z.array(z.enum(['github'])),
  institutions: z.array(
    z.object({
      connectionId: z.string(),
      displayName: z.string(),
      federation: z.object({
        key: z.string(),
        label: z.string(),
      }),
      connected: z.boolean(),
    }),
  ),
  hasTotp: z.boolean(),
  sessions: z.array(
    z.object({
      id: z.uuid(),
      type: z.enum(['regular', 'impersonation', 'mfa']),
      userId: z.uuid(),
      deviceName: z.string().max(255).nullable(),
      deviceType: z.enum(['desktop', 'mobile']),
      deviceOs: z.string().max(255).nullable(),
      browser: z.string().max(255).nullable(),
      authStrategy: z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic']),
      connectionId: z.uuid().nullable(),
      ipHash: z.string().max(64).nullable(),
      ipSubnetHash: z.string().max(64).nullable(),
      ipCountry: z.string().max(2).nullable(),
      ipAsn: z.int().gte(-2147483648).lte(2147483647).nullable(),
      deviceIdHash: z.string().max(64).nullable(),
      createdAt: z.string(),
      expiresAt: z.string(),
      revokedAt: z.string().nullable(),
      revokedBy: z.uuid().nullable(),
      revocationReason: z.enum(['sign_out', 'other_session', 'mfa_enabled', 'session_cap', 'replaced', 'impersonation_stopped']).nullable(),
      impersonatorSessionId: z.uuid().nullable(),
      steppedUpAt: z.string().nullable(),
      steppedUpVia: z.enum(['passkey', 'totp', 'email']).nullable(),
      isCurrent: z.boolean(),
      isNewDevice: z.boolean(),
    }),
  ),
  passkeys: z.array(
    z.object({
      id: z.uuid(),
      userId: z.uuid(),
      deviceName: z.string().max(255).nullable(),
      deviceType: z.enum(['desktop', 'mobile']),
      deviceOs: z.string().max(255).nullable(),
      browser: z.string().max(255).nullable(),
      nameOnDevice: z.string().max(255),
      createdAt: z.string(),
    }),
  ),
});

/**
 * An invitation to join a channel that is not accepted yet: the invited email, the role and who invited. It becomes a membership once accepted; the signed-in user's invitations list it with the channel it is for.
 */
export const zInactiveMembership = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  tenantId: z.string().max(24),
  channelType: z.enum(['organization', 'workspace', 'project']),
  channelId: z.uuid(),
  email: z.string().max(255),
  userId: z.uuid().nullable(),
  role: z.enum(['admin', 'member', 'guest']),
  rejectedAt: z.string().nullable(),
  remindedAt: z.string().nullable(),
  createdBy: zActorRef.nullable(),
  organizationId: z.uuid(),
  workspaceId: z.uuid().nullable(),
  projectId: z.uuid().nullable(),
});

/**
 * Permission to upload files with one upload template, signed for the upload service, with the storage prefix the files land under. A client requests one before uploading; the template decides whether files are stored public or private.
 */
export const zUploadToken = z.object({
  publicBucket: z.boolean(),
  sub: z.string(),
  s3: z.boolean(),
  signature: z.string().nullable(),
  params: z
    .object({
      auth: z.object({
        key: z.string(),
        expires: z.string().optional(),
      }),
    })
    .nullable(),
});

/**
 * An app the signed-in user consented to: its OAuth client, the scopes it may use and when the consent expires. Listed under Connected apps in account settings, where the user can revoke it.
 */
export const zConnectedApp = z.object({
  id: z.string(),
  clientId: z.string(),
  clientName: z.string(),
  scopes: z.array(z.string()),
  resources: z.array(z.string()),
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
});

/**
 * A project that organizes tasks and members within an organization.
 */
export const zProject = z.object({
  id: z.uuid(),
  entityType: z.enum(['project']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  slug: z.string().max(255),
  thumbnailUrl: z.string().max(2048).nullable(),
  bannerUrl: z.string().max(2048).nullable(),
  publishedAt: z.string().nullable(),
  public: z.boolean(),
  toolsConfig: z.union([z.string(), z.number(), z.boolean(), z.record(z.string(), z.unknown()), z.array(z.unknown())]).nullable(),
  path: z.string().nullable(),
  organizationId: z.uuid(),
  included: z.object({
    membership: zMembershipBase.optional(),
    counts: z
      .object({
        membership: z.object({
          admin: z.number(),
          member: z.number(),
          guest: z.number(),
          pending: z.number(),
          total: z.number(),
        }),
        entities: z.object({
          task: z.number(),
          label: z.number(),
          attachment: z.number(),
        }),
        entitiesSelf: z.object({
          task: z.number(),
          label: z.number(),
          attachment: z.number(),
        }),
        activity: z.object({
          task: z.object({
            created: z.number().nullable(),
            updated: z.number().nullable(),
          }),
          label: z.object({
            created: z.number().nullable(),
            updated: z.number().nullable(),
          }),
          attachment: z.object({
            created: z.number().nullable(),
            updated: z.number().nullable(),
          }),
        }),
        taskStatusCounts: z
          .object({
            accepted: z.number(),
            reviewed: z.number(),
            delivered: z.number(),
            finished: z.number(),
            started: z.number(),
            unstarted: z.number(),
            iced: z.number(),
          })
          .optional(),
      })
      .optional(),
    members: z.array(zUserMinimalBase).optional(),
  }),
});

/**
 * A message from the public site: a contact message, a waitlist sign-up or a newsletter subscription. Anyone can send one, signed in or not; system admins list them.
 */
export const zRequest = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  message: z.string().nullable(),
  email: z.string().max(255),
  type: z.enum(['waitlist', 'newsletter', 'contact']),
  wasInvited: z.boolean(),
});

/**
 * A task representing a unit of work, assignable to users with status tracking and labels.
 */
export const zTask = z.object({
  id: z.uuid(),
  entityType: z.enum(['task']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  description: z.string().max(1000000).nullable(),
  keywords: z.string().max(1000000),
  deletedAt: z.string().nullable(),
  deletedBy: zActorRef.nullable(),
  public: z.boolean(),
  seq: z.int().gte(-9007199254740991).lte(9007199254740991),
  expandable: z.boolean(),
  summary: z.string().max(1000000),
  summaryLength: z.int().gte(-2147483648).lte(2147483647),
  primaryLabelId: z.uuid(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327),
  status: z.union([z.literal(6), z.literal(5), z.literal(4), z.literal(3), z.literal(2), z.literal(1), z.literal(0)]),
  statusChangedAt: z.string(),
  checkboxCount: z.int().gte(-2147483648).lte(2147483647),
  checkedCount: z.int().gte(-2147483648).lte(2147483647),
  attachments: z.array(z.uuid()),
  projectId: z.uuid(),
  organizationId: z.uuid(),
  labels: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      color: z.string().nullable(),
      mode: z.enum(['primary', 'secondary', 'epic']),
      icon: z.string().nullable(),
      projectId: z.string(),
    }),
  ),
  primaryLabel: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      color: z.string().nullable(),
      mode: z.enum(['primary', 'secondary', 'epic']),
      icon: z.string().nullable(),
      projectId: z.string(),
    })
    .nullable(),
  assignedTo: z.array(zUserMinimalBase),
  stx: zStxBase,
});

/**
 * The top-level isolation and billing boundary. Tenant and organization are 1:1: a tenant holds one organization, or none until that organization is created. System admins list and update tenants; a signed-in user can create one of their own.
 */
export const zTenant = z.object({
  id: z.string().max(24),
  name: z.string().max(255),
  status: z.enum(['active', 'suspended', 'archived']),
  restrictions: z.object({
    quotas: z.record(z.string(), z.int().gte(0)),
    rateLimits: z.object({
      apiPointsPerHour: z.int().gte(0),
    }),
    allowUnregisteredClients: z.boolean(),
  }),
  authStrategies: z.array(z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic'])),
  subscriptionId: z.string().max(255).nullable(),
  subscriptionStatus: z.enum(['none', 'trialing', 'active', 'past_due', 'paused', 'canceled']),
  subscriptionPlan: z.string().max(255).nullable(),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  organization: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      thumbnailUrl: z.string().nullable(),
      entityType: z.enum(['organization']),
    })
    .nullable(),
});

/**
 * How an OAuth client gets an access token for this API or an MCP endpoint (RFC 9728): the authorization servers that issue tokens and the scopes accepted. A client finds it through the `WWW-Authenticate` header of a 401 response.
 */
export const zProtectedResourceMetadata = z.object({
  resource: z.string(),
  authorization_servers: z.array(z.string()),
  scopes_supported: z.array(z.string()),
  bearer_methods_supported: z.array(z.string()),
  resource_documentation: z.string(),
});

/**
 * The channel entity every other channel nests under, owning memberships and roles. Tenant and organization are 1:1: an organization belongs to one tenant, and that tenant holds no other. Returned with `included` data: the signed-in user's membership, counts and member previews.
 */
export const zOrganization = z.object({
  id: z.uuid(),
  entityType: z.enum(['organization']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  slug: z.string().max(255),
  thumbnailUrl: z.string().max(2048).nullable(),
  bannerUrl: z.string().max(2048).nullable(),
  publishedAt: z.string().nullable(),
  public: z.boolean(),
  toolsConfig: z.object({
    'organization.settings': z
      .object({
        order: z.array(z.string()).optional(),
        hidden: z.array(z.string()).optional(),
      })
      .optional(),
    'organization.tabs': z
      .object({
        order: z.array(z.string()).optional(),
        hidden: z.array(z.string()).optional(),
      })
      .optional(),
    'workspace.settings': z
      .object({
        order: z.array(z.string()).optional(),
        hidden: z.array(z.string()).optional(),
      })
      .optional(),
    'workspace.tabs': z
      .object({
        order: z.array(z.string()).optional(),
        hidden: z.array(z.string()).optional(),
      })
      .optional(),
    'project.settings': z
      .object({
        order: z.array(z.string()).optional(),
        hidden: z.array(z.string()).optional(),
      })
      .optional(),
    'project.tabs': z
      .object({
        order: z.array(z.string()).optional(),
        hidden: z.array(z.string()).optional(),
      })
      .optional(),
  }),
  path: z.string().nullable(),
  shortName: z.string().max(255).nullable(),
  country: z.string().max(255).nullable(),
  timezone: z.string().max(255).nullable(),
  defaultLanguage: z.enum(['en', 'nl']),
  languages: z.array(z.enum(['en', 'nl'])).min(1),
  notificationEmail: z.string().max(255).nullable(),
  color: z.string().max(255).nullable(),
  logoUrl: z.string().max(2048).nullable(),
  websiteUrl: z.string().max(2048).nullable(),
  welcomeText: z.string().max(1000000).nullable(),
  chatSupport: z.boolean(),
  organizationFlags: z.record(z.string(), z.unknown()),
  setupConfig: z.object({
    primaryLabels: z
      .array(
        z.object({
          slug: z.string().max(255),
          name: z
            .string()
            .min(1)
            .max(255)
            .regex(/^[^\p{Cc}<>]+$/u),
          color: z.enum(['red', 'orange', 'amber', 'yellow', 'green', 'emerald', 'teal', 'sky', 'blue', 'indigo', 'violet', 'pink', 'slate']),
          icon: z.string().max(255).nullable(),
        }),
      )
      .min(1)
      .max(6),
  }),
  included: z.object({
    membership: zMembershipBase.optional(),
    counts: z
      .object({
        membership: z.object({
          admin: z.number(),
          member: z.number(),
          guest: z.number(),
          pending: z.number(),
          total: z.number(),
        }),
        entities: z.object({
          workspace: z.number(),
          project: z.number(),
          task: z.number(),
          label: z.number(),
          attachment: z.number(),
        }),
        entitiesSelf: z.object({
          workspace: z.number(),
          project: z.number(),
          task: z.number(),
          label: z.number(),
          attachment: z.number(),
        }),
        activity: z.object({
          task: z.object({
            created: z.number().nullable(),
            updated: z.number().nullable(),
          }),
          label: z.object({
            created: z.number().nullable(),
            updated: z.number().nullable(),
          }),
          attachment: z.object({
            created: z.number().nullable(),
            updated: z.number().nullable(),
          }),
        }),
      })
      .optional(),
    members: z.array(zUserMinimalBase).optional(),
  }),
});

/**
 * A personal workspace that groups projects and tasks within an organization.
 */
export const zWorkspace = z.object({
  id: z.uuid(),
  entityType: z.enum(['workspace']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  slug: z.string().max(255),
  thumbnailUrl: z.string().max(2048).nullable(),
  bannerUrl: z.string().max(2048).nullable(),
  publishedAt: z.string().nullable(),
  public: z.boolean(),
  toolsConfig: z.union([z.string(), z.number(), z.boolean(), z.record(z.string(), z.unknown()), z.array(z.unknown())]).nullable(),
  path: z.string().nullable(),
  organizationId: z.uuid(),
  included: z.object({
    membership: zMembershipBase.optional(),
    counts: z
      .object({
        membership: z.object({
          admin: z.number(),
          member: z.number(),
          guest: z.number(),
          pending: z.number(),
          total: z.number(),
        }),
        entities: z.record(z.string(), z.unknown()),
        entitiesSelf: z.record(z.string(), z.unknown()),
        activity: z.record(z.string(), z.unknown()),
      })
      .optional(),
    members: z.array(zUserMinimalBase).optional(),
  }),
});

/**
 * A file in an organization: a product entity that syncs to clients and works offline. It holds the file metadata and storage keys, not the file itself; a client fetches the file through a signed URL.
 */
export const zAttachment = z.object({
  id: z.uuid(),
  entityType: z.enum(['attachment']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  stx: zStxBase,
  description: z.string().max(1000000).nullable(),
  keywords: z.string().max(1000000),
  deletedAt: z.string().nullable(),
  deletedBy: zActorRef.nullable(),
  public: z.boolean(),
  seq: z.int().gte(-9007199254740991).lte(9007199254740991),
  publicBucket: z.boolean(),
  bucketName: z.string().max(255),
  groupId: z.uuid().nullable(),
  filename: z.string().max(255),
  contentType: z.string().max(255),
  convertedContentType: z.string().max(255).nullable(),
  size: z.string().max(255),
  keys: z.object({
    original: z.string(),
    preview: z.string().optional(),
    thumbnail: z.string().optional(),
    converted: z.string().optional(),
  }),
  projectId: z.uuid(),
  organizationId: z.uuid(),
  viewCount: z.int().gte(0).optional(),
});

/**
 * A label used to categorize and filter tasks within a project or workspace.
 */
export const zLabel = z.object({
  id: z.uuid(),
  entityType: z.enum(['label']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  description: z.string().max(1000000).nullable(),
  keywords: z.string().max(1000000),
  deletedAt: z.string().nullable(),
  deletedBy: z.uuid().nullable(),
  public: z.boolean(),
  seq: z.int().gte(-9007199254740991).lte(9007199254740991),
  color: z.string().max(255).nullable(),
  mode: z.enum(['primary', 'secondary', 'epic']),
  slug: z.string().max(255),
  icon: z.string().max(255).nullable(),
  organizationTracked: z.boolean(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327).nullable(),
  projectId: z.uuid(),
  organizationId: z.uuid(),
  stx: zStxBase,
});

export const zInviteOutcome = z.object({
  email: z.email(),
  outcome: z.enum(['invite', 'remind', 'add', 'member']),
  invitedAt: z.string().optional(),
  declinedAt: z.string().optional(),
  role: z.enum(['admin', 'member', 'guest']).optional(),
});

/**
 * An actor that is not a person, in one tenant: API keys run as it, and it holds role bindings like a member holds memberships. An organization admin creates one, for example with Create API key in organization settings.
 */
export const zServiceAccount = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  status: z.enum(['active', 'disabled']),
  bindings: z.array(
    z.object({
      channelType: z.enum(['organization', 'workspace', 'project']),
      channelId: z.string().max(50),
      organizationId: z.string().max(50),
      role: z.enum(['admin', 'member', 'guest']),
    }),
  ),
  oauthClientId: z.string().max(255).nullable(),
  createdAt: z.string(),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  lastSeenAt: z.string().nullable(),
});

/**
 * A secret key a service account authenticates with, sent as `Authorization: Bearer` or `x-api-key`. Only its prefix and last four characters are returned after creation; it can expire and be revoked.
 */
export const zApiKey = z.object({
  id: z.uuid(),
  actorId: z.uuid(),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  prefix: z.string().max(255),
  last4: z.string().max(4),
  scopes: z
    .array(
      z.enum([
        'organization:read',
        'organization:write',
        'workspace:read',
        'workspace:write',
        'project:read',
        'project:write',
        'task:read',
        'task:write',
        'label:read',
        'label:write',
        'attachment:read',
        'attachment:write',
      ]),
    )
    .nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedBy: z.uuid().nullable(),
  createdBy: zActorRef.nullable(),
  createdAt: z.string(),
});

/**
 * Auth health status
 */
export const zGetAuthHealthResponse = z.object({
  restrictedMode: z.boolean(),
  retryAfter: z.number().optional(),
  federations: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
    }),
  ),
});

export const zCheckEmailBody = z.object({
  email: z.email().min(4).max(255),
});

/**
 * Whether this browser is recognized for the address
 */
export const zCheckEmailResponse = z.object({
  recognized: z.boolean(),
});

export const zInvokeTokenPath = z.object({
  type: z.enum(['oauth-verification', 'invitation', 'magic', 'step-up']),
  token: z.string(),
});

export const zGetTokenDataPath = z.object({
  type: z.enum(['oauth-verification', 'invitation', 'magic', 'step-up']),
  id: z.string().max(50),
});

/**
 * Token is valid
 */
export const zGetTokenDataResponse = z.object({
  email: z.email(),
  userId: z.string().optional(),
  inactiveMembershipId: z.string().optional(),
  ssoConnectionId: z.string().optional(),
  invitation: z
    .object({
      entityType: z.enum(['organization', 'workspace', 'project']),
      entityName: z.string(),
      role: z.enum(['admin', 'member', 'guest']),
      inviterName: z.string(),
    })
    .optional(),
});

/**
 * Invitation was accepted
 */
export const zAcceptInvitationTokenResponse = zChannelBase;

export const zStartImpersonationBody = z.object({
  targetUserId: z.string().max(50),
});

/**
 * Impersonating
 */
export const zStartImpersonationResponse = z.void();

/**
 * Stopped impersonating
 */
export const zStopImpersonationResponse = z.void();

export const zResendInvitationWithTokenBody = z.object({
  tokenId: z.uuid(),
});

/**
 * Invitation email sent when the invitation is pending
 */
export const zResendInvitationWithTokenResponse = z.void();

/**
 * User signed out
 */
export const zSignOutResponse = z.void();

export const zSendMagicLinkBody = z.object({
  email: z.email().min(4).max(255),
  redirect: z.string().optional(),
});

/**
 * Magic link email sent (or silently ignored if email not found)
 */
export const zSendMagicLinkResponse = z.void();

/**
 * The full address the held link signs in, as the confirm page shows it
 */
export const zGetPendingMagicLinkResponse = z.object({
  email: z.string(),
});

/**
 * Challenge created
 */
export const zGenerateTotpKeyResponse = z.object({
  totpUri: z.string(),
  manualKey: z.string(),
});

/**
 * TOTP deleted
 */
export const zDeleteTotpResponse = z.void();

export const zCreateTotpBody = z.object({
  code: z.string().regex(/^\d{6}$/),
});

export const zSignInWithTotpBody = z.object({
  code: z.string().regex(/^\d{6}$/),
});

/**
 * TOTP verified
 */
export const zSignInWithTotpResponse = z.void();

export const zCreatePasskeyBody = z.object({
  attestation: z.object({
    id: z.string(),
    rawId: z.string(),
    response: z.object({
      clientDataJSON: z.string(),
      attestationObject: z.string(),
      authenticatorData: z.string().optional(),
      transports: z.array(z.string()).optional(),
      publicKeyAlgorithm: z.number().optional(),
      publicKey: z.string().optional(),
    }),
    authenticatorAttachment: z.enum(['cross-platform', 'platform']).optional(),
    clientExtensionResults: z.unknown().optional(),
    type: z.enum(['public-key']),
  }),
  nameOnDevice: z.string().max(255),
});

/**
 * Passkey created
 */
export const zCreatePasskeyResponse = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  deviceName: z.string().max(255).nullable(),
  deviceType: z.enum(['desktop', 'mobile']),
  deviceOs: z.string().max(255).nullable(),
  browser: z.string().max(255).nullable(),
  nameOnDevice: z.string().max(255),
  createdAt: z.string(),
});

export const zDeletePasskeyPath = z.object({
  id: z.string().max(50),
});

/**
 * Passkey deleted
 */
export const zDeletePasskeyResponse = z.void();

export const zGeneratePasskeyChallengeBody = z.object({
  type: z.enum(['authentication', 'mfa', 'registration']),
});

/**
 * Challenge generated
 */
export const zGeneratePasskeyChallengeResponse = z.object({
  challenge: z.string(),
  credentialIds: z.array(z.string()),
});

export const zSignInWithPasskeyBody = z.object({
  assertion: z.object({
    id: z.string(),
    rawId: z.string(),
    response: z.object({
      clientDataJSON: z.string(),
      authenticatorData: z.string(),
      signature: z.string(),
      userHandle: z.string().optional(),
    }),
    authenticatorAttachment: z.enum(['cross-platform', 'platform']).optional(),
    clientExtensionResults: z.unknown().optional(),
    type: z.enum(['public-key']),
  }),
  type: z.enum(['authentication', 'mfa']),
});

/**
 * Passkey verified
 */
export const zSignInWithPasskeyResponse = z.void();

/**
 * Connect pinned
 */
export const zStartOAuthConnectResponse = z.void();

export const zGithubQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zGoogleQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zMicrosoftQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zGithubCallbackQuery = z.object({
  code: z.string().optional(),
  state: z.string(),
  error: z.string().optional(),
  error_description: z.string().optional(),
  error_uri: z.string().optional(),
});

export const zGoogleCallbackQuery = z.object({
  code: z.string().optional(),
  state: z.string(),
  error: z.string().optional(),
  error_description: z.string().optional(),
  error_uri: z.string().optional(),
});

export const zMicrosoftCallbackQuery = z.object({
  code: z.string().optional(),
  state: z.string(),
  error: z.string().optional(),
  error_description: z.string().optional(),
  error_uri: z.string().optional(),
});

export const zGetSsoEntryPath = z.object({
  connectionId: z.string().max(50),
});

/**
 * SSO entry
 */
export const zGetSsoEntryResponse = zSsoEntry;

export const zStartSsoPath = z.object({
  connectionId: z.string().max(50),
});

export const zStartSsoQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zStartSsoFederationPath = z.object({
  federation: z.string().min(1).max(64),
});

export const zStartSsoFederationQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zSsoCallbackQuery = z.object({
  code: z.string().optional(),
  state: z.string(),
  error: z.string().optional(),
  error_description: z.string().optional(),
  error_uri: z.string().optional(),
});

/**
 * The address the link was sent to
 */
export const zSendSsoRecoveryLinkResponse = z.object({
  email: z.string(),
});

/**
 * Step-up state
 */
export const zGetStepUpResponse = z.object({
  steppedUp: z.boolean(),
  methods: z.array(z.enum(['passkey', 'totp', 'email', 'sign_in'])),
});

export const zStepUpBody = z.object({
  passkeyData: z
    .object({
      id: z.string(),
      rawId: z.string(),
      response: z.object({
        clientDataJSON: z.string(),
        authenticatorData: z.string(),
        signature: z.string(),
        userHandle: z.string().optional(),
      }),
      authenticatorAttachment: z.enum(['cross-platform', 'platform']).optional(),
      clientExtensionResults: z.unknown().optional(),
      type: z.enum(['public-key']),
    })
    .optional(),
  totpCode: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});

/**
 * Session stepped up
 */
export const zStepUpResponse = z.void();

/**
 * Challenge issued
 */
export const zGetStepUpPasskeyChallengeResponse = z.object({
  challenge: z.string(),
  credentialIds: z.array(z.string()),
});

export const zSendStepUpLinkBody = z.object({
  redirect: z.string().optional(),
});

/**
 * Link sent
 */
export const zSendStepUpLinkResponse = z.void();

export const zGetConnectionsPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Connections
 */
export const zGetConnectionsResponse = z.array(zConnection);

export const zCreateConnectionBody = z.object({
  issuer: z.string(),
  displayName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u),
  claimValues: z
    .array(
      z
        .string()
        .min(4)
        .max(255)
        .regex(/^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/),
    )
    .min(1)
    .max(20),
  idpEntityIds: z.array(z.string().min(1).max(255)).min(1).max(10),
  status: z.enum(['pending', 'active', 'disabled']).optional(),
  jitProvisioning: z.boolean().optional(),
  logoUrl: z.url().max(255).optional(),
});

export const zCreateConnectionPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Created connection
 */
export const zCreateConnectionResponse = zConnection;

export const zDeleteConnectionPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Removed connection
 */
export const zDeleteConnectionResponse = zConnection;

export const zUpdateConnectionBody = z.object({
  displayName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .optional(),
  claimValues: z
    .array(
      z
        .string()
        .min(4)
        .max(255)
        .regex(/^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/),
    )
    .min(1)
    .max(20)
    .optional(),
  idpEntityIds: z.array(z.string().min(1).max(255)).min(1).max(10).optional(),
  status: z.enum(['pending', 'active', 'disabled']).optional(),
  jitProvisioning: z.boolean().optional(),
  logoUrl: z.url().max(255).optional(),
});

export const zUpdateConnectionPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Updated connection
 */
export const zUpdateConnectionResponse = zConnection;

export const zCheckSlugBody = z.object({
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-zA-Z0-9]+(-{0,3}[a-zA-Z0-9]+)*$/),
  entityType: z.enum(['organization', 'workspace', 'project']),
});

export const zCheckSlugPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Slug is available
 */
export const zCheckSlugResponse = z.void();

export const zPostAppCatchupBody = z.object({
  cursor: z.string().optional(),
  views: z
    .array(
      z.object({
        key: z.string().max(512),
        organizationId: z.string(),
        prefixes: z.array(z.string().max(512)).min(1).max(64),
        entityTypes: z.array(z.enum(['task', 'label', 'attachment'])).min(1),
        depth: z.enum(['self', 'subtree']).optional(),
        cursor: z.int().gte(0),
      }),
    )
    .max(256)
    .optional(),
});

/**
 * Catchup summary
 */
export const zPostAppCatchupResponse = z.object({
  changes: z.record(
    z.string(),
    z.object({
      signals: z
        .object({
          membership: z.int().optional(),
        })
        .optional(),
      propagation: z
        .array(
          z.object({
            embeddedProduct: z.enum(['task', 'label', 'attachment']),
            hostProduct: z.enum(['task', 'label', 'attachment']),
            hostColumn: z.string(),
            update: z.array(z.string()),
            remove: z.array(z.string()),
          }),
        )
        .optional(),
    }),
  ),
  views: z
    .array(
      z.object({
        key: z.string(),
        status: z.enum(['ok', 'opaque', 'forbidden']),
        frontiers: z.record(z.string(), z.int()).optional(),
        counts: z.record(z.string(), z.int()).optional(),
      }),
    )
    .optional(),
  cursor: z.string().nullable(),
  generation: z.int().optional(),
});

/**
 * User deleted
 */
export const zDeleteMeResponse = z.void();

/**
 * User
 */
export const zGetMeResponse = zMe;

export const zUpdateMeBody = z.object({
  bannerUrl: z.string().max(2048).nullish(),
  contrast: z.enum(['system', 'more']).optional(),
  description: z.string().max(1000000).nullish(),
  firstName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .nullish(),
  lastName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .nullish(),
  language: z.enum(['en', 'nl']).optional(),
  newsletter: z.boolean().optional(),
  thumbnailUrl: z.string().max(2048).nullish(),
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-zA-Z0-9]+(-{0,3}[a-zA-Z0-9]+)*$/)
    .optional(),
  userFlags: z
    .object({
      finishedOnboarding: z.boolean().optional(),
    })
    .optional(),
});

/**
 * User
 */
export const zUpdateMeResponse = zUser;

export const zToggleMfaBody = z.object({
  mfaRequired: z.boolean(),
});

/**
 * User
 */
export const zToggleMfaResponse = zUser;

/**
 * User sign-up info
 */
export const zGetMyAuthResponse = zMeAuthData;

/**
 * Invitations pending
 */
export const zGetMyInvitationsResponse = z.object({
  items: z.array(
    z.object({
      entity: zChannelBase,
      inactiveMembership: zInactiveMembership,
    }),
  ),
  total: z.number(),
});

export const zRevokeMySessionsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

/**
 * Sessions were revoked
 */
export const zRevokeMySessionsResponse = z.object({
  data: z.array(
    z.object({
      id: z.uuid(),
      type: z.enum(['regular', 'impersonation', 'mfa']),
      userId: z.uuid(),
      deviceName: z.string().max(255).nullable(),
      deviceType: z.enum(['desktop', 'mobile']),
      deviceOs: z.string().max(255).nullable(),
      browser: z.string().max(255).nullable(),
      authStrategy: z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic']),
      connectionId: z.uuid().nullable(),
      ipHash: z.string().max(64).nullable(),
      ipSubnetHash: z.string().max(64).nullable(),
      ipCountry: z.string().max(2).nullable(),
      ipAsn: z.int().gte(-2147483648).lte(2147483647).nullable(),
      deviceIdHash: z.string().max(64).nullable(),
      createdAt: z.string(),
      expiresAt: z.string(),
      revokedAt: z.string().nullable(),
      revokedBy: z.uuid().nullable(),
      revocationReason: z.enum(['sign_out', 'other_session', 'mfa_enabled', 'session_cap', 'replaced', 'impersonation_stopped']).nullable(),
      impersonatorSessionId: z.uuid().nullable(),
      steppedUpAt: z.string().nullable(),
      steppedUpVia: z.enum(['passkey', 'totp', 'email']).nullable(),
    }),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zDeleteMyMembershipQuery = z.object({
  entityId: z.string().max(50),
  entityType: z.enum(['organization', 'workspace', 'project']),
});

/**
 * Membership removed
 */
export const zDeleteMyMembershipResponse = z.void();

export const zGetUploadTokenQuery = z.object({
  organizationId: z.uuid().optional(),
  templateId: z.enum(['avatar', 'cover', 'attachment', 'newsletter']),
});

/**
 * Upload token with a scope for a user or organization
 */
export const zGetUploadTokenResponse = zUploadToken;

/**
 * User memberships
 */
export const zGetMyMembershipsResponse = z.object({
  items: z.array(zMembershipBase),
});

/**
 * Connected apps
 */
export const zGetConnectedAppsResponse = z.object({
  items: z.array(zConnectedApp),
});

export const zRevokeConnectedAppPath = z.object({
  id: z.string().max(50),
});

/**
 * Consent was revoked
 */
export const zRevokeConnectedAppResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

/**
 * Public counts
 */
export const zGetPublicCountsResponse = z.object({
  user: z.number(),
  organization: z.number(),
  workspace: z.number(),
  project: z.number(),
  task: z.number(),
  label: z.number(),
  attachment: z.number(),
});

export const zGetNotificationsQuery = z.object({
  unread: z.enum(['true', 'false']).optional(),
  limit: z.int().gte(1).lte(100).optional().default(30),
  before: z.string().optional(),
});

/**
 * Notifications and unread count
 */
export const zGetNotificationsResponse = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      type: z.enum(['mention', 'comment', 'reply']),
      entityType: z.enum(['task', 'label', 'attachment']),
      subjectId: z.string(),
      contextId: z.string().nullable(),
      channelId: z.string(),
      channelType: z.enum(['organization', 'workspace', 'project']),
      organizationId: z.string(),
      tenantId: z.string(),
      actorId: z.string().nullable(),
      actor: zUserMinimalBase.nullable(),
      channelName: z.string(),
      subjectTitle: z.string(),
      readAt: z.string().nullable(),
    }),
  ),
  unreadCount: z.int().gte(0),
});

export const zMarkNotificationsReadBody = z.object({
  ids: z.array(z.string().max(50)).max(200).optional(),
  contextId: z.string().max(50).optional(),
});

/**
 * Number of notifications marked read
 */
export const zMarkNotificationsReadResponse = z.object({
  updated: z.int().gte(0),
});

/**
 * Notification preferences
 */
export const zGetNotificationPreferencesResponse = z.object({
  mentionEmail: z.boolean(),
  commentEmail: z.boolean(),
  digest: z.enum(['off', 'daily', 'weekly']),
});

export const zUpdateNotificationPreferencesBody = z.object({
  mentionEmail: z.boolean().optional(),
  commentEmail: z.boolean().optional(),
  digest: z.enum(['off', 'daily', 'weekly']).optional(),
});

/**
 * Updated notification preferences
 */
export const zUpdateNotificationPreferencesResponse = z.object({
  mentionEmail: z.boolean(),
  commentEmail: z.boolean(),
  digest: z.enum(['off', 'daily', 'weekly']),
});

export const zUnsubscribeNotificationsQuery = z.object({
  user: z.string().max(50),
  category: z.enum(['digest', 'mention', 'comment', 'newsletter']),
  token: z.string(),
});

export const zGetPublicProjectPath = z.object({
  id: z.string().max(50),
});

export const zGetPublicProjectQuery = z.object({
  slug: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
});

/**
 * A project that organizes tasks and members within an organization.
 */
export const zGetPublicProjectResponse = zProject.and(
  z.object({
    membership: z.null(),
  }),
);

/**
 * VAPID public key
 */
export const zGetPushVapidResponse = z.object({
  publicKey: z.string().nullable(),
});

export const zDeletePushSubscriptionQuery = z.object({
  endpoint: z.url().max(2048),
});

/**
 * Number of subscriptions removed
 */
export const zDeletePushSubscriptionResponse = z.object({
  deleted: z.int().gte(0),
});

export const zCreatePushSubscriptionBody = z.object({
  endpoint: z.url().max(2048),
  expirationTime: z.number().nullish(),
  keys: z.object({
    p256dh: z.string().min(1).max(512),
    auth: z.string().min(1).max(512),
  }),
});

/**
 * Stored subscription
 */
export const zCreatePushSubscriptionResponse = z.object({
  id: z.string(),
  endpoint: z.string(),
});

export const zDeleteRequestsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

/**
 * Success
 */
export const zDeleteRequestsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetRequestsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'email', 'type', 'createdAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
});

/**
 * Requests
 */
export const zGetRequestsResponse = z.object({
  items: z.array(zRequest),
  total: z.number(),
});

export const zCreateRequestBody = z.object({
  email: z.email().min(4).max(255),
  type: z.enum(['waitlist', 'newsletter', 'contact']),
  message: z.string().max(5000).nullable(),
});

/**
 * Request received
 */
export const zCreateRequestResponse = z.void();

/**
 * Unseen counts per parent channel entity per entity type
 */
export const zGetUnseenCountsResponse = z.record(z.string(), z.record(z.string(), z.int().gte(0)));

export const zSystemInviteBody = z.object({
  emails: z.array(z.email().min(4).max(255)).min(1).max(50),
});

/**
 * Invitations are sent
 */
export const zSystemInviteResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  invitesSentCount: z.number(),
});

export const zDeleteUsersBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

/**
 * Success
 */
export const zDeleteUsersResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zUpdateUserBody = z.object({
  bannerUrl: z.string().max(2048).nullish(),
  contrast: z.enum(['system', 'more']).optional(),
  description: z.string().max(1000000).nullish(),
  firstName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .nullish(),
  lastName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .nullish(),
  language: z.enum(['en', 'nl']).optional(),
  newsletter: z.boolean().optional(),
  thumbnailUrl: z.string().max(2048).nullish(),
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-zA-Z0-9]+(-{0,3}[a-zA-Z0-9]+)*$/)
    .optional(),
});

export const zUpdateUserPath = z.object({
  id: z.string().max(50),
});

/**
 * User
 */
export const zUpdateUserResponse = zUser;

export const zSendNewsletterBody = z.object({
  organizationIds: z.array(z.uuid()).max(50),
  roles: z
    .array(z.enum(['admin', 'member', 'guest']))
    .min(1)
    .max(3),
  subject: z.string().max(255),
  content: z.string().max(1000000),
});

export const zSendNewsletterQuery = z.object({
  toSelf: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
});

/**
 * Newsletter sent
 */
export const zSendNewsletterResponse = z.void();

export const zGetPublicTaskPath = z.object({
  id: z.string().max(50),
});

/**
 * Task
 */
export const zGetPublicTaskResponse = zTask;

export const zGetPublicTasksQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['projectId', 'status', 'createdBy', 'updatedAt', 'createdAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  matchMode: z.enum(['all', 'any']).optional().default('all'),
  acceptedCutOff: z.number().gt(0).optional(),
  projectId: z.string().max(50),
});

/**
 * Tasks
 */
export const zGetPublicTasksResponse = z.object({
  items: z.array(zTask),
  total: z.number(),
});

export const zResolveTaskLinkPath = z.object({
  id: z.string().max(50),
});

/**
 * Task link resolution data
 */
export const zResolveTaskLinkResponse = z.object({
  taskId: z.string(),
  projectId: z.string(),
  projectSlug: z.string(),
  organizationId: z.string(),
  organizationSlug: z.string(),
  tenantId: z.string(),
  public: z.boolean(),
});

export const zGetTaskCoverPath = z.object({
  id: z.string().max(50),
});

export const zRedirectToTaskPath = z.object({
  id: z.string().max(50),
});

export const zGetTenantsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['createdAt', 'name']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  status: z.enum(['active', 'suspended', 'archived']).optional(),
});

/**
 * Tenants list
 */
export const zGetTenantsResponse = z.object({
  items: z.array(zTenant),
  total: z.number(),
});

export const zSelfCreateTenantBody = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u),
});

/**
 * Created tenant
 */
export const zSelfCreateTenantResponse = zTenant;

export const zUpdateTenantBody = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .optional(),
  status: z.enum(['active', 'suspended', 'archived']).optional(),
  subscriptionId: z.string().max(255).nullish(),
  subscriptionStatus: z.enum(['none', 'trialing', 'active', 'past_due', 'paused', 'canceled']).optional(),
  subscriptionPlan: z.string().max(255).nullish(),
  authStrategies: z.array(z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic'])).optional(),
  restrictions: z
    .object({
      quotas: z.record(z.string(), z.int().gte(0)).optional(),
      allowUnregisteredClients: z.boolean().optional(),
      rateLimits: z
        .object({
          apiPointsPerHour: z.int().gte(0).optional(),
        })
        .optional(),
    })
    .optional(),
});

export const zUpdateTenantPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Updated tenant
 */
export const zUpdateTenantResponse = zTenant;

export const zGetUsersQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'name', 'email', 'role', 'createdAt', 'lastSeenAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  role: z.enum(['admin']).optional(),
});

/**
 * Users
 */
export const zGetUsersResponse = z.object({
  items: z.array(
    zUserBase.and(
      z.object({
        lastSeenAt: z.string().nullable(),
        role: z.enum(['admin']).nullish(),
      }),
    ),
  ),
  total: z.number(),
});

export const zGetUserPath = z.object({
  relatableUserId: z.string().max(50),
});

export const zGetUserQuery = z.object({
  slug: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
  include: z.string().optional(),
});

/**
 * The user fields shown to every user who shares an organization with this one: name, slug, profile images, description and email address. User lookups and member lists return it with the last-seen time added; member lists add the membership too.
 */
export const zGetUserResponse = zUserBase.and(
  z.object({
    lastSeenAt: z.string().nullable(),
    mfaRequired: z.boolean().optional(),
  }),
);

export const zGetApiProtectedResourceMetadataPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Protected resource metadata
 */
export const zGetApiProtectedResourceMetadataResponse = zProtectedResourceMetadata;

export const zDeleteOrganizationsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

export const zDeleteOrganizationsPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteOrganizationsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zCreateOrganizationsBody = z
  .array(
    z.object({
      id: z
        .string()
        .max(50)
        .regex(/^temp-/),
      name: z
        .string()
        .min(1)
        .max(255)
        .regex(/^[^\p{Cc}<>]+$/u),
      slug: z
        .string()
        .min(2)
        .max(255)
        .regex(/^[a-zA-Z0-9]+(-{0,3}[a-zA-Z0-9]+)*$/),
      draft: z.boolean().optional(),
    }),
  )
  .min(1)
  .max(10);

export const zCreateOrganizationsPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Organizations were created
 */
export const zCreateOrganizationsResponse = z.object({
  data: z.array(
    zOrganization.and(
      z.object({
        included: z
          .object({
            membership: zMembershipBase,
            counts: z
              .object({
                membership: z.object({
                  admin: z.number(),
                  member: z.number(),
                  guest: z.number(),
                  pending: z.number(),
                  total: z.number(),
                }),
                entities: z.object({
                  workspace: z.number(),
                  project: z.number(),
                  task: z.number(),
                  label: z.number(),
                  attachment: z.number(),
                }),
                entitiesSelf: z.object({
                  workspace: z.number(),
                  project: z.number(),
                  task: z.number(),
                  label: z.number(),
                  attachment: z.number(),
                }),
                activity: z.object({
                  task: z.object({
                    created: z.number().nullable(),
                    updated: z.number().nullable(),
                  }),
                  label: z.object({
                    created: z.number().nullable(),
                    updated: z.number().nullable(),
                  }),
                  attachment: z.object({
                    created: z.number().nullable(),
                    updated: z.number().nullable(),
                  }),
                }),
              })
              .optional(),
            members: z.array(zUserMinimalBase).optional(),
          })
          .optional(),
      }),
    ),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetOrganizationsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'name', 'createdAt', 'displayOrder']).optional().default('displayOrder'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  relatableUserId: z.uuid().optional(),
  role: z.enum(['admin', 'member', 'guest']).optional(),
  excludeArchived: z.enum(['true', 'false']).optional(),
  include: z.string().optional(),
});

/**
 * Organizations
 */
export const zGetOrganizationsResponse = z.object({
  items: z.array(zOrganization),
  total: z.number(),
});

export const zGetOrganizationPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

export const zGetOrganizationQuery = z.object({
  slug: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
  include: z.string().optional(),
});

/**
 * Organization
 */
export const zGetOrganizationResponse = zOrganization;

export const zUpdateOrganizationBody = z.object({
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-zA-Z0-9]+(-{0,3}[a-zA-Z0-9]+)*$/)
    .optional(),
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .optional(),
  shortName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .nullish(),
  country: z.string().max(255).nullish(),
  timezone: z.string().max(255).nullish(),
  defaultLanguage: z.enum(['en', 'nl']).optional(),
  languages: z
    .array(z.enum(['en', 'nl']))
    .min(1)
    .optional(),
  notificationEmail: z.string().max(255).nullish(),
  color: z.string().max(255).nullish(),
  thumbnailUrl: z.string().max(2048).nullish(),
  logoUrl: z.string().max(2048).nullish(),
  bannerUrl: z.string().max(2048).nullish(),
  websiteUrl: z.string().max(2048).nullish(),
  welcomeText: z.string().max(1000000).nullish(),
  chatSupport: z.boolean().optional(),
  organizationFlags: z.record(z.string(), z.unknown()).optional(),
  setupConfig: z
    .object({
      primaryLabels: z
        .array(
          z.object({
            slug: z.string().max(255),
            name: z
              .string()
              .min(1)
              .max(255)
              .regex(/^[^\p{Cc}<>]+$/u),
            color: z.enum(['red', 'orange', 'amber', 'yellow', 'green', 'emerald', 'teal', 'sky', 'blue', 'indigo', 'violet', 'pink', 'slate']),
            icon: z.string().max(255).nullable(),
          }),
        )
        .min(1)
        .max(6)
        .optional(),
    })
    .optional(),
  toolsConfig: z
    .object({
      'organization.settings': z
        .object({
          order: z.array(z.string()).optional(),
          hidden: z.array(z.string()).optional(),
        })
        .optional(),
      'organization.tabs': z
        .object({
          order: z.array(z.string()).optional(),
          hidden: z.array(z.string()).optional(),
        })
        .optional(),
      'workspace.settings': z
        .object({
          order: z.array(z.string()).optional(),
          hidden: z.array(z.string()).optional(),
        })
        .optional(),
      'workspace.tabs': z
        .object({
          order: z.array(z.string()).optional(),
          hidden: z.array(z.string()).optional(),
        })
        .optional(),
      'project.settings': z
        .object({
          order: z.array(z.string()).optional(),
          hidden: z.array(z.string()).optional(),
        })
        .optional(),
      'project.tabs': z
        .object({
          order: z.array(z.string()).optional(),
          hidden: z.array(z.string()).optional(),
        })
        .optional(),
    })
    .optional(),
});

export const zUpdateOrganizationPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Organization was updated
 */
export const zUpdateOrganizationResponse = zOrganization;

export const zPublishOrganizationPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Organization was published
 */
export const zPublishOrganizationResponse = z.object({
  organization: zOrganization,
  invitationsSent: z.int().gte(0),
});

export const zDeleteProjectsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

export const zDeleteProjectsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteProjectsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zCreateProjectsBody = z
  .array(
    z.object({
      id: z
        .string()
        .max(50)
        .regex(/^temp-/),
      name: z
        .string()
        .min(1)
        .max(255)
        .regex(/^[^\p{Cc}<>]+$/u),
      slug: z
        .string()
        .min(2)
        .max(255)
        .regex(/^[a-zA-Z0-9]+(-{0,3}[a-zA-Z0-9]+)*$/),
      public: z.boolean(),
    }),
  )
  .min(1)
  .max(10);

export const zCreateProjectsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zCreateProjectsQuery = z.object({
  workspaceId: z.string().max(50),
});

/**
 * Projects created
 */
export const zCreateProjectsResponse = z.object({
  data: z.array(
    zProject.and(
      z.object({
        included: z
          .object({
            membership: zMembershipBase,
            counts: z
              .object({
                membership: z.object({
                  admin: z.number(),
                  member: z.number(),
                  guest: z.number(),
                  pending: z.number(),
                  total: z.number(),
                }),
                entities: z.object({
                  task: z.number(),
                  label: z.number(),
                  attachment: z.number(),
                }),
                entitiesSelf: z.object({
                  task: z.number(),
                  label: z.number(),
                  attachment: z.number(),
                }),
                activity: z.object({
                  task: z.object({
                    created: z.number().nullable(),
                    updated: z.number().nullable(),
                  }),
                  label: z.object({
                    created: z.number().nullable(),
                    updated: z.number().nullable(),
                  }),
                  attachment: z.object({
                    created: z.number().nullable(),
                    updated: z.number().nullable(),
                  }),
                }),
                taskStatusCounts: z
                  .object({
                    accepted: z.number(),
                    reviewed: z.number(),
                    delivered: z.number(),
                    finished: z.number(),
                    started: z.number(),
                    unstarted: z.number(),
                    iced: z.number(),
                  })
                  .optional(),
              })
              .optional(),
            members: z.array(zUserMinimalBase).optional(),
          })
          .optional(),
      }),
    ),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetProjectsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'name', 'createdAt', 'displayOrder']).optional().default('displayOrder'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  organizationId: z.string().max(50).optional(),
  workspaceId: z.string().max(50).optional(),
  relatableUserId: z.string().max(50).optional(),
  role: z.enum(['admin', 'member', 'guest']).optional(),
  excludeArchived: z.enum(['true', 'false']).optional(),
  scope: z.enum(['memberships', 'organization']).optional().default('memberships'),
  include: z.string().optional(),
});

/**
 * Projects
 */
export const zGetProjectsResponse = z.object({
  items: z.array(zProject),
  total: z.number(),
});

export const zGetProjectPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

export const zGetProjectQuery = z.object({
  slug: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
  include: z.string().optional(),
});

/**
 * Project
 */
export const zGetProjectResponse = zProject;

export const zUpdateProjectBody = z.object({
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-zA-Z0-9]+(-{0,3}[a-zA-Z0-9]+)*$/)
    .optional(),
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .optional(),
  thumbnailUrl: z.string().max(2048).nullish(),
  bannerUrl: z.string().max(2048).nullish(),
  public: z.boolean().optional(),
});

export const zUpdateProjectPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Project updated
 */
export const zUpdateProjectResponse = zProject;

export const zAssignProjectWorkspacePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

export const zAssignProjectWorkspaceQuery = z.object({
  workspaceId: z.string().max(50),
});

/**
 * A project that organizes tasks and members within an organization.
 */
export const zAssignProjectWorkspaceResponse = zProject.and(
  z.object({
    included: z
      .object({
        membership: zMembershipBase,
        counts: z
          .object({
            membership: z.object({
              admin: z.number(),
              member: z.number(),
              guest: z.number(),
              pending: z.number(),
              total: z.number(),
            }),
            entities: z.object({
              task: z.number(),
              label: z.number(),
              attachment: z.number(),
            }),
            entitiesSelf: z.object({
              task: z.number(),
              label: z.number(),
              attachment: z.number(),
            }),
            activity: z.object({
              task: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
              label: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
              attachment: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
            }),
            taskStatusCounts: z
              .object({
                accepted: z.number(),
                reviewed: z.number(),
                delivered: z.number(),
                finished: z.number(),
                started: z.number(),
                unstarted: z.number(),
                iced: z.number(),
              })
              .optional(),
          })
          .optional(),
        members: z.array(zUserMinimalBase).optional(),
      })
      .optional(),
  }),
);

export const zRemoveProjectWorkspacePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * A project that organizes tasks and members within an organization.
 */
export const zRemoveProjectWorkspaceResponse = zProject.and(
  z.object({
    included: z
      .object({
        membership: zMembershipBase,
        counts: z
          .object({
            membership: z.object({
              admin: z.number(),
              member: z.number(),
              guest: z.number(),
              pending: z.number(),
              total: z.number(),
            }),
            entities: z.object({
              task: z.number(),
              label: z.number(),
              attachment: z.number(),
            }),
            entitiesSelf: z.object({
              task: z.number(),
              label: z.number(),
              attachment: z.number(),
            }),
            activity: z.object({
              task: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
              label: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
              attachment: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
            }),
            taskStatusCounts: z
              .object({
                accepted: z.number(),
                reviewed: z.number(),
                delivered: z.number(),
                finished: z.number(),
                started: z.number(),
                unstarted: z.number(),
                iced: z.number(),
              })
              .optional(),
          })
          .optional(),
        members: z.array(zUserMinimalBase).optional(),
      })
      .optional(),
  }),
);

export const zMoveProjectToWorkspacePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

export const zMoveProjectToWorkspaceQuery = z.object({
  workspaceId: z.string().max(50),
});

/**
 * A project that organizes tasks and members within an organization.
 */
export const zMoveProjectToWorkspaceResponse = zProject.and(
  z.object({
    included: z
      .object({
        membership: zMembershipBase,
        counts: z
          .object({
            membership: z.object({
              admin: z.number(),
              member: z.number(),
              guest: z.number(),
              pending: z.number(),
              total: z.number(),
            }),
            entities: z.object({
              task: z.number(),
              label: z.number(),
              attachment: z.number(),
            }),
            entitiesSelf: z.object({
              task: z.number(),
              label: z.number(),
              attachment: z.number(),
            }),
            activity: z.object({
              task: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
              label: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
              attachment: z.object({
                created: z.number().nullable(),
                updated: z.number().nullable(),
              }),
            }),
            taskStatusCounts: z
              .object({
                accepted: z.number(),
                reviewed: z.number(),
                delivered: z.number(),
                finished: z.number(),
                started: z.number(),
                unstarted: z.number(),
                iced: z.number(),
              })
              .optional(),
          })
          .optional(),
        members: z.array(zUserMinimalBase).optional(),
      })
      .optional(),
  }),
);

export const zDeleteWorkspacesBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

export const zDeleteWorkspacesPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteWorkspacesResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zCreateWorkspacesBody = z
  .array(
    z.object({
      id: z
        .string()
        .max(50)
        .regex(/^temp-/),
      name: z
        .string()
        .min(1)
        .max(255)
        .regex(/^[^\p{Cc}<>]+$/u),
    }),
  )
  .min(1)
  .max(10);

export const zCreateWorkspacesPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Workspaces created
 */
export const zCreateWorkspacesResponse = z.object({
  data: z.array(
    zWorkspace.and(
      z.object({
        included: z
          .object({
            membership: zMembershipBase,
            counts: z
              .object({
                membership: z.object({
                  admin: z.number(),
                  member: z.number(),
                  guest: z.number(),
                  pending: z.number(),
                  total: z.number(),
                }),
                entities: z.record(z.string(), z.unknown()),
                entitiesSelf: z.record(z.string(), z.unknown()),
                activity: z.record(z.string(), z.unknown()),
              })
              .optional(),
            members: z.array(zUserMinimalBase).optional(),
          })
          .optional(),
      }),
    ),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetWorkspacesQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'name', 'createdAt', 'displayOrder']).optional().default('displayOrder'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  organizationId: z.string().max(50).optional(),
  role: z.enum(['admin', 'member', 'guest']).optional(),
  excludeArchived: z.enum(['true', 'false']).optional(),
  include: z.string().optional(),
});

/**
 * Workspaces
 */
export const zGetWorkspacesResponse = z.object({
  items: z.array(zWorkspace),
  total: z.number(),
});

export const zGetWorkspacePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

export const zGetWorkspaceQuery = z.object({
  slug: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
  include: z.string().optional(),
});

/**
 * Workspace
 */
export const zGetWorkspaceResponse = zWorkspace;

export const zUpdateWorkspaceBody = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .optional(),
});

export const zUpdateWorkspacePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Workspace updated
 */
export const zUpdateWorkspaceResponse = zWorkspace;

export const zDeleteAttachmentsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
  stx: z
    .object({
      mutationId: z.string(),
      sourceId: z.string(),
    })
    .optional(),
});

export const zDeleteAttachmentsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteAttachmentsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetAttachmentsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetAttachmentsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['name', 'createdAt', 'contentType']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  afterSeq: z.int().gte(0).optional(),
  untilSeq: z.int().gte(0).optional(),
  channelId: z.uuid().optional(),
});

/**
 * Attachments
 */
export const zGetAttachmentsResponse = z.object({
  items: z.array(zAttachment),
  total: z.number(),
});

export const zCreateAttachmentsBody = z
  .array(
    z.object({
      id: z.uuid(),
      name: z.string().max(255).optional(),
      filename: z.string().max(255),
      contentType: z.string().max(255),
      size: z.string().max(255),
      keys: z.object({
        original: z.string(),
        preview: z.string().optional(),
        thumbnail: z.string().optional(),
        converted: z.string().optional(),
      }),
      groupId: z.uuid().nullish(),
      convertedContentType: z.string().max(255).nullish(),
      projectId: z.uuid(),
      stx: zStxBase,
    }),
  )
  .min(1)
  .max(50);

export const zCreateAttachmentsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zCreateAttachmentsResponse = z.union([
  z.object({
    data: z.array(zAttachment),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
  z.object({
    data: z.array(zAttachment),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
]);

export const zGetPresignedUrlsBody = z.object({
  items: z
    .array(
      z.object({
        attachmentId: z.uuid(),
        variant: z.enum(['original', 'preview', 'thumbnail', 'converted']).optional().default('original'),
      }),
    )
    .min(1)
    .max(50),
});

export const zGetPresignedUrlsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Presigned URLs
 */
export const zGetPresignedUrlsResponse = z.object({
  data: z.array(
    z.object({
      attachmentId: z.uuid(),
      variant: z.enum(['original', 'preview', 'thumbnail', 'converted']),
      url: z.string(),
    }),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetAttachmentPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Attachment
 */
export const zGetAttachmentResponse = zAttachment;

export const zUpdateAttachmentBody = z.object({
  ops: z.object({
    name: z.string().max(255).optional(),
    description: z.string().max(1000000).optional(),
  }),
  stx: zStxBase,
});

export const zUpdateAttachmentPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

export const zUpdateAttachmentQuery = z.object({
  fullResponse: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
});

/**
 * Attachment was updated
 */
export const zUpdateAttachmentResponse = zAttachment;

export const zLinkAttachmentKeysBody = z.object({
  keys: z.object({
    original: z.string(),
    preview: z.string().optional(),
    thumbnail: z.string().optional(),
    converted: z.string().optional(),
  }),
  convertedContentType: z.string().max(255).nullish(),
});

export const zLinkAttachmentKeysPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Attachment with its stored file
 */
export const zLinkAttachmentKeysResponse = zAttachment;

export const zDeleteLabelsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
  stx: z
    .object({
      mutationId: z.string(),
      sourceId: z.string(),
    })
    .optional(),
});

export const zDeleteLabelsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteLabelsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetLabelsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetLabelsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['name']).optional().default('name'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  afterSeq: z.int().gte(0).optional(),
  untilSeq: z.int().gte(0).optional(),
  modes: z.string().optional(),
  projectId: z.string().max(50).optional(),
  workspaceId: z.string().max(50).optional(),
});

/**
 * Label list
 */
export const zGetLabelsResponse = z.object({
  items: z.array(zLabel),
  total: z.number(),
});

export const zCreateLabelsBody = z
  .array(
    z.object({
      name: z.string().max(255).optional(),
      id: z.uuid(),
      color: z.string().max(255).nullable(),
      mode: z.enum(['primary', 'secondary', 'epic']).optional().default('secondary'),
      slug: z.string().max(255).optional(),
      icon: z.string().max(255).nullish(),
      displayOrder: z.number().optional(),
      projectId: z.uuid(),
      stx: zStxBase,
    }),
  )
  .min(1)
  .max(50);

export const zCreateLabelsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zCreateLabelsResponse = z.union([
  z.object({
    data: z.array(zLabel),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
  z.object({
    data: z.array(zLabel),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
]);

export const zGetLabelPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Label
 */
export const zGetLabelResponse = zLabel;

export const zUpdateLabelBody = z.object({
  ops: z.object({
    name: z.string().max(255).optional(),
    color: z.string().max(255).nullish(),
    slug: z.string().max(255).optional(),
    icon: z.string().max(255).nullish(),
    displayOrder: z.number().optional(),
    description: z.string().max(1000000).nullish(),
    mode: z.enum(['secondary', 'epic']).optional(),
    organizationTracked: z.boolean().optional(),
  }),
  stx: zStxBase,
});

export const zUpdateLabelPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Label updated
 */
export const zUpdateLabelResponse = zLabel;

export const zGetMcpProtectedResourceMetadataPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Protected resource metadata
 */
export const zGetMcpProtectedResourceMetadataResponse = zProtectedResourceMetadata;

export const zHandleMcpBody = z.unknown();

export const zHandleMcpPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zDeleteMembershipsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

export const zDeleteMembershipsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zDeleteMembershipsQuery = z.object({
  entityId: z.string().max(50),
  entityType: z.enum(['organization', 'workspace', 'project']),
});

/**
 * Success
 */
export const zDeleteMembershipsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zMembershipInviteBody = z.object({
  emails: z.array(z.email().min(4).max(255)).min(1).max(50),
  role: z.enum(['admin', 'member', 'guest']),
});

export const zMembershipInvitePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zMembershipInviteQuery = z.object({
  entityId: z.string().max(50),
  entityType: z.enum(['organization', 'workspace', 'project']),
});

/**
 * What happened to each address
 */
export const zMembershipInviteResponse = z.object({
  data: z.array(
    z.object({
      id: z.uuid(),
      tenantId: z.string().max(24),
      channelType: z.enum(['organization', 'workspace', 'project']),
      channelId: z.uuid(),
      userId: z.uuid(),
      role: z.enum(['admin', 'member', 'guest']),
      organizationId: z.uuid(),
      workspaceId: z.uuid().nullable(),
      projectId: z.uuid().nullable(),
      archived: z.boolean().optional(),
      muted: z.boolean().optional(),
      displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
    }),
  ),
  outcomes: z.array(zInviteOutcome),
  held: z.boolean(),
});

export const zCheckMembershipInviteBody = z.object({
  emails: z.array(z.email().min(4).max(255)).min(1).max(50),
});

export const zCheckMembershipInvitePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zCheckMembershipInviteQuery = z.object({
  entityId: z.string().max(50),
  entityType: z.enum(['organization', 'workspace', 'project']),
});

/**
 * What an invite would do with each address
 */
export const zCheckMembershipInviteResponse = z.object({
  outcomes: z.array(zInviteOutcome),
});

export const zUpdateMembershipBody = z.object({
  role: z.enum(['admin', 'member', 'guest']).optional(),
  archived: z.boolean().optional(),
  muted: z.boolean().optional(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
});

export const zUpdateMembershipPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Membership updated
 */
export const zUpdateMembershipResponse = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  tenantId: z.string().max(24),
  channelType: z.enum(['organization', 'workspace', 'project']),
  channelId: z.uuid(),
  userId: z.uuid(),
  role: z.enum(['admin', 'member', 'guest']),
  createdBy: zActorRef.nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: zActorRef.nullable(),
  organizationId: z.uuid(),
  workspaceId: z.uuid().nullable(),
  projectId: z.uuid().nullable(),
  archived: z.boolean().optional(),
  muted: z.boolean().optional(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
});

export const zHandleMembershipInvitationPath = z.object({
  id: z.string().max(50),
  acceptOrReject: z.enum(['accept', 'reject']),
});

/**
 * Invitation was accepted
 */
export const zHandleMembershipInvitationResponse = zChannelBase;

export const zGetMembersPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetMembersQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'name', 'email', 'role', 'createdAt', 'lastSeenAt', 'lastPostedAt']).optional().default('lastSeenAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  entityId: z.string().max(50),
  entityType: z.enum(['organization', 'workspace', 'project']),
  role: z.enum(['admin', 'member', 'guest']).optional(),
  include: z.string().optional(),
  userIds: z.string().optional(),
});

/**
 * Members
 */
export const zGetMembersResponse = z.object({
  items: z.array(
    zUserBase.and(
      z.object({
        lastSeenAt: z.string().nullable(),
        membership: z.object({
          id: z.uuid(),
          tenantId: z.string().max(24),
          channelType: z.enum(['organization', 'workspace', 'project']),
          channelId: z.uuid(),
          userId: z.uuid(),
          role: z.enum(['admin', 'member', 'guest']),
          organizationId: z.uuid(),
          workspaceId: z.uuid().nullable(),
          projectId: z.uuid().nullable(),
          archived: z.boolean().optional(),
          muted: z.boolean().optional(),
          displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
        }),
        counts: z
          .object({
            memberships: z.object({
              workspace: z.number().optional(),
              project: z.number().optional(),
            }),
            products: z.object({
              task: z.number(),
            }),
            activity: z.object({
              task: z.number().nullable(),
            }),
          })
          .optional(),
        mfaRequired: z.boolean().optional(),
      }),
    ),
  ),
  total: z.number(),
});

export const zGetPendingMembershipsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetPendingMembershipsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['createdAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  entityId: z.string().max(50),
  entityType: z.enum(['organization', 'workspace', 'project']),
});

/**
 * Pending memberships
 */
export const zGetPendingMembershipsResponse = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      email: z.email(),
      role: z.enum(['admin', 'member', 'guest']).nullable(),
      createdAt: z.string(),
      remindedAt: z.string().nullable(),
      createdBy: zActorRef.nullable(),
    }),
  ),
  total: z.number(),
});

export const zResendPendingInvitationPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Invitation resent
 */
export const zResendPendingInvitationResponse = z.void();

export const zWithdrawPendingInvitationPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Invitation withdrawn
 */
export const zWithdrawPendingInvitationResponse = z.void();

export const zMarkSeenBody = z.object({
  entityIds: z.array(z.string().max(50)).min(1).max(500),
  entityType: z.enum(['task', 'label', 'attachment']),
});

export const zMarkSeenPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Seen records processed
 */
export const zMarkSeenResponse = z.object({
  newCount: z.int().gte(0),
});

export const zGetServiceAccountsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetServiceAccountsQuery = z.object({
  q: z.string().max(255).optional(),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
});

/**
 * Service accounts
 */
export const zGetServiceAccountsResponse = z.object({
  items: z.array(zServiceAccount),
  total: z.number(),
});

export const zCreateServiceAccountBody = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u),
  role: z.enum(['admin', 'member']),
  key: z
    .object({
      name: z
        .string()
        .min(1)
        .max(255)
        .regex(/^[^\p{Cc}<>]+$/u),
      scopes: z
        .array(
          z.enum([
            'organization:read',
            'organization:write',
            'workspace:read',
            'workspace:write',
            'project:read',
            'project:write',
            'task:read',
            'task:write',
            'label:read',
            'label:write',
            'attachment:read',
            'attachment:write',
          ]),
        )
        .min(1)
        .nullish(),
      expiresAt: z.iso.datetime().optional(),
    })
    .optional(),
});

export const zCreateServiceAccountPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Service account was created
 */
export const zCreateServiceAccountResponse = z.object({
  serviceAccount: zServiceAccount,
  apiKey: zApiKey
    .and(
      z.object({
        secret: z.string(),
      }),
    )
    .optional(),
});

export const zUpdateServiceAccountBody = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u)
    .optional(),
  status: z.enum(['active', 'disabled']).optional(),
});

export const zUpdateServiceAccountPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Service account was updated
 */
export const zUpdateServiceAccountResponse = zServiceAccount;

export const zGetApiKeysPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * API keys
 */
export const zGetApiKeysResponse = z.object({
  items: z.array(zApiKey),
});

export const zCreateApiKeyBody = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[^\p{Cc}<>]+$/u),
  scopes: z
    .array(
      z.enum([
        'organization:read',
        'organization:write',
        'workspace:read',
        'workspace:write',
        'project:read',
        'project:write',
        'task:read',
        'task:write',
        'label:read',
        'label:write',
        'attachment:read',
        'attachment:write',
      ]),
    )
    .min(1)
    .nullish(),
  expiresAt: z.iso.datetime().optional(),
  rollFrom: z.string().max(50).optional(),
  rollOverlapDays: z.int().gte(0).lte(30).optional().default(7),
});

export const zCreateApiKeyPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * A newly created API key with its full secret, returned this once only.
 */
export const zCreateApiKeyResponse = zApiKey.and(
  z.object({
    secret: z.string(),
  }),
);

export const zRevokeApiKeyPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
  keyId: z.string().max(50),
});

/**
 * API key was revoked
 */
export const zRevokeApiKeyResponse = zApiKey;

export const zDeleteTasksBody = z.object({
  ids: z.array(z.string()).min(1).max(100),
  stx: z
    .object({
      mutationId: z.string(),
      sourceId: z.string(),
    })
    .optional(),
});

export const zDeleteTasksPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteTasksResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetTasksPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetTasksQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['projectId', 'status', 'createdBy', 'updatedAt', 'createdAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
  offset: z.int().gte(0).optional().default(0),
  limit: z.int().gte(1).lte(1000).optional().default(40),
  afterSeq: z.int().gte(0).optional(),
  untilSeq: z.int().gte(0).optional(),
  matchMode: z.enum(['all', 'any']).optional().default('all'),
  acceptedCutOff: z.number().gt(0).optional(),
  projectId: z.string().max(50).optional(),
  workspaceId: z.string().max(50).optional(),
});

/**
 * Tasks
 */
export const zGetTasksResponse = z.object({
  items: z.array(zTask),
  total: z.number(),
});

export const zCreateTasksBody = z
  .array(
    z.object({
      name: z.string().max(255).optional(),
      description: z.string().max(1000000).nullable(),
      id: z.uuid(),
      status: z.union([z.literal(6), z.literal(5), z.literal(4), z.literal(3), z.literal(2), z.literal(1), z.literal(0)]),
      primaryLabelId: z.uuid().optional(),
      displayOrder: z.number().optional(),
      labels: z.array(z.uuid()).max(50).optional(),
      assignedTo: z.array(z.uuid()).max(50).optional(),
      projectId: z.uuid(),
      stx: zStxBase,
    }),
  )
  .min(1)
  .max(50);

export const zCreateTasksPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zCreateTasksResponse = z.union([
  z.object({
    data: z.array(zTask),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
  z.object({
    data: z.array(zTask),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
]);

export const zGetTaskPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Tasks
 */
export const zGetTaskResponse = zTask;

export const zUpdateTaskBody = z.object({
  ops: z.object({
    name: z.string().max(255).optional(),
    description: z.string().max(1000000).nullish(),
    status: z.union([z.literal(6), z.literal(5), z.literal(4), z.literal(3), z.literal(2), z.literal(1), z.literal(0)]).optional(),
    primaryLabelId: z.uuid().optional(),
    displayOrder: z.number().optional(),
    labels: z
      .object({
        add: z.array(z.uuid()).max(50).optional().default([]),
        remove: z.array(z.uuid()).max(50).optional().default([]),
      })
      .optional(),
    assignedTo: z
      .object({
        add: z.array(z.uuid()).max(50).optional().default([]),
        remove: z.array(z.uuid()).max(50).optional().default([]),
      })
      .optional(),
    projectId: z.uuid().optional(),
    public: z.boolean().optional(),
  }),
  stx: zStxBase,
});

export const zUpdateTaskPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

export const zUpdateTaskQuery = z.object({
  fullResponse: z
    .union([z.enum(['true', 'false']), z.boolean()])
    .optional()
    .default('false'),
});

/**
 * Task updated
 */
export const zUpdateTaskResponse = zTask;

export const zGetYjsTokenPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetYjsTokenQuery = z.object({
  entityType: z.enum(['task', 'label', 'attachment']),
  entityId: z.string().max(50),
});

/**
 * Yjs auth token
 */
export const zGetYjsTokenResponse = z.object({
  token: z.string(),
});

export const zPullYjsDocumentBody = z.object({
  entityType: z.enum(['task', 'label', 'attachment']),
  entityId: z.string().max(50),
  stateVector: z.string().max(262144),
});

export const zPullYjsDocumentPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Document diff
 */
export const zPullYjsDocumentResponse = z.object({
  generation: z.uuid(),
  update: z.string(),
  stateVector: z.string(),
});

export const zPushYjsUpdateBody = z.object({
  entityType: z.enum(['task', 'label', 'attachment']),
  entityId: z.string().max(50),
  generation: z.uuid(),
  update: z.string().max(699051),
});

export const zPushYjsUpdatePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Saved
 */
export const zPushYjsUpdateResponse = z.object({
  status: z.enum(['appended', 'empty']),
});
